// BounceHouseLandingPage.tsx
import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  Navbar,
  Nav,
  Badge,
} from "react-bootstrap";
import {
  BalloonHeart,
  PeopleFill,
  GeoAltFill,
  TelephoneFill,
  StarFill,
  CalendarEvent,
  ClockHistory,
  ShieldCheck,
  Truck,
  CashCoin,
  EmojiSmile,
  LightningChargeFill,
} from "react-bootstrap-icons";

// Make sure in your main entry (e.g. index.tsx) you have:
// import "bootstrap/dist/css/bootstrap.min.css";

const BounceHouseLandingPage_2025_11_21_chat: React.FC = () => {
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    // TODO: integrate with your backend / email / form handler
    alert("Thanks! We’ll bounce back to you with a quote shortly. 🎈");
  };

  return (
    <>
      {/* NAVBAR */}
      <Navbar bg="light" expand="lg" sticky="top" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#hero" className="fw-bold">
            <BalloonHeart className="me-2" />
            <span>HappyBounce Rentals</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="main-navbar" />
          <Navbar.Collapse id="main-navbar">
            <Nav className="ms-auto">
              <Nav.Link href="#packages">Packages</Nav.Link>
              <Nav.Link href="#why-us">Why Us</Nav.Link>
              <Nav.Link href="#gallery">Gallery</Nav.Link>
              <Nav.Link href="#faq">FAQ</Nav.Link>
              <Nav.Link href="#quote">
                <Button size="sm" variant="primary">
                  Get a Quote
                </Button>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO SECTION */}
      <section
        id="hero"
        className="py-5"
        style={{
          background:
            "linear-gradient(135deg, rgba(13,110,253,0.08), rgba(255,193,7,0.15))",
        }}
      >
        <Container>
          <Row className="align-items-center gy-4">
            <Col md={7}>
              <Badge bg="warning" text="dark" className="mb-3">
                <EmojiSmile className="me-1" />
                Serving Birthdays, School Events & More
              </Badge>
              <h1 className="display-5 fw-bold mb-3">
                Make Your Next Party{" "}
                <span className="text-primary">Unforgettable</span>
              </h1>
              <p className="lead text-muted mb-4">
                Safe, clean, and colorful bounce houses delivered right to your
                door. Perfect for birthdays, school events, church parties, and
                community festivals.
              </p>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  variant="primary"
                  size="lg"
                  href="#quote"
                  className="d-flex align-items-center"
                >
                  <LightningChargeFill className="me-2" />
                  Get Instant Quote
                </Button>
                <Button
                  variant="outline-primary"
                  size="lg"
                  href="tel:18005551234"
                  className="d-flex align-items-center"
                >
                  <TelephoneFill className="me-2" />
                  Call (800) 555-1234
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-4 mt-3 small text-muted">
                <div className="d-flex align-items-center">
                  <ShieldCheck className="me-2 text-success" />
                  Fully insured & safety inspected
                </div>
                <div className="d-flex align-items-center">
                  <Truck className="me-2 text-primary" />
                  Delivery, setup & pickup included
                </div>
                <div className="d-flex align-items-center">
                  <StarFill className="me-1 text-warning" />
                  <StarFill className="me-1 text-warning" />
                  <StarFill className="me-1 text-warning" />
                  <StarFill className="me-1 text-warning" />
                  <StarFill className="me-1 text-warning" />
                  <span>Rated 5.0 by local families</span>
                </div>
              </div>
            </Col>
            <Col md={5}>
              {/* Hero “highlight” card */}
              <Card className="shadow-lg border-0">
                <Card.Body>
                  <h5 className="fw-bold mb-3">
                    Quick Party Details
                    <Badge bg="primary" className="ms-2">
                      New
                    </Badge>
                  </h5>
                  <p className="small text-muted mb-3">
                    Answer a few quick questions and we’ll confirm availability
                    and pricing.
                  </p>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="eventType">
                      <Form.Label>Event Type</Form.Label>
                      <Form.Select required>
                        <option value="">Choose an event type</option>
                        <option>Birthday Party</option>
                        <option>School Event</option>
                        <option>Church Event</option>
                        <option>Corporate / Company Picnic</option>
                        <option>Community Festival</option>
                        <option>Other</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="eventDate">
                      <Form.Label>Event Date</Form.Label>
                      <Form.Control type="date" required />
                    </Form.Group>
                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-3" controlId="guestCount">
                          <Form.Label>Approx. Kids</Form.Label>
                          <Form.Control
                            type="number"
                            min={1}
                            placeholder="e.g. 15"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-3" controlId="zipCode">
                          <Form.Label>ZIP Code</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. 80211"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3" controlId="contactEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="you@example.com"
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="contactPhone">
                      <Form.Label>Mobile Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="(555) 555-5555"
                        required
                      />
                    </Form.Group>
                    <Button type="submit" variant="primary" className="w-100">
                      Check Availability
                    </Button>
                    <p className="mt-2 mb-0 text-muted small">
                      No spam. We only use your info to confirm your rental.
                    </p>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PACKAGES / POPULAR UNITS */}
      <section id="packages" className="py-5 bg-light">
        <Container>
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-2">Popular Bounce House Packages</h2>
            <p className="text-muted mb-0">
              Simple, transparent pricing with delivery, setup, and teardown
              included.
            </p>
          </div>
          <Row className="gy-4">
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex flex-column">
                  <Badge bg="info" className="mb-2">
                    Best for Small Parties
                  </Badge>
                  <Card.Title className="fw-bold">
                    Classic Castle
                  </Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    Up to 12 jumpers
                  </Card.Subtitle>
                  <h3 className="mt-3 mb-3">
                    $189 <small className="text-muted fs-6">/ 4 hours</small>
                  </h3>
                  <ul className="list-unstyled small flex-grow-1">
                    <li className="mb-1">
                      <StarFill className="me-2 text-warning" />
                      13&apos; x 13&apos; bounce area
                    </li>
                    <li className="mb-1">
                      <ShieldCheck className="me-2 text-success" />
                      Safety step &amp; netted walls
                    </li>
                    <li className="mb-1">
                      <ClockHistory className="me-2" />
                      Add extra hour for $25
                    </li>
                  </ul>
                  <Button variant="outline-primary" href="#quote">
                    Reserve Classic
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 shadow border-primary border-2 position-relative">
                <Badge
                  bg="primary"
                  className="position-absolute top-0 start-50 translate-middle rounded-pill px-3 py-1"
                >
                  Most Popular
                </Badge>
                <Card.Body className="d-flex flex-column pt-4">
                  <Card.Title className="fw-bold">
                    Slide Combo Castle
                  </Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    Bounce + Slide + Hoop
                  </Card.Subtitle>
                  <h3 className="mt-3 mb-3">
                    $249 <small className="text-muted fs-6">/ 4 hours</small>
                  </h3>
                  <ul className="list-unstyled small flex-grow-1">
                    <li className="mb-1">
                      <PeopleFill className="me-2" />
                      Up to 15 kids at a time
                    </li>
                    <li className="mb-1">
                      <Truck className="me-2" />
                      Free local delivery
                    </li>
                    <li className="mb-1">
                      <CashCoin className="me-2" />
                      Weekend specials available
                    </li>
                  </ul>
                  <Button variant="primary" href="#quote">
                    Book Slide Combo
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex flex-column">
                  <Badge bg="success" className="mb-2">
                    Big Events
                  </Badge>
                  <Card.Title className="fw-bold">
                    Mega Obstacle Course
                  </Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    Perfect for schools &amp; festivals
                  </Card.Subtitle>
                  <h3 className="mt-3 mb-3">
                    $399 <small className="text-muted fs-6">/ 4 hours</small>
                  </h3>
                  <ul className="list-unstyled small flex-grow-1">
                    <li className="mb-1">
                      <CalendarEvent className="me-2" />
                      School &amp; church discounts
                    </li>
                    <li className="mb-1">
                      <PeopleFill className="me-2" />
                      High throughput fun
                    </li>
                    <li className="mb-1">
                      <ShieldCheck className="me-2 text-success" />
                      Fully supervised options available
                    </li>
                  </ul>
                  <Button variant="outline-primary" href="#quote">
                    Check Date &amp; Price
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* WHY US */}
      <section id="why-us" className="py-5">
        <Container>
          <Row className="align-items-center gy-4">
            <Col md={6}>
              <h2 className="fw-bold mb-3">Why Families Love HappyBounce</h2>
              <p className="text-muted">
                We know your party is a big deal. That’s why we treat every
                event like our own kid’s birthday—on time, spotless equipment,
                and friendly service.
              </p>
              <Row className="gy-3 mt-2">
                <Col sm={6}>
                  <div className="d-flex">
                    <ShieldCheck className="me-3 fs-3 text-success" />
                    <div>
                      <h6 className="fw-bold mb-1">Safety First</h6>
                      <p className="small text-muted mb-0">
                        Fully insured, regularly inspected units, and clear
                        safety rules for every rental.
                      </p>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex">
                    <ClockHistory className="me-3 fs-3" />
                    <div>
                      <h6 className="fw-bold mb-1">On-Time Guarantee</h6>
                      <p className="small text-muted mb-0">
                        We arrive early so your bounce house is fully set up
                        before guests show up.
                      </p>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex">
                    <Truck className="me-3 fs-3" />
                    <div>
                      <h6 className="fw-bold mb-1">Delivery Included</h6>
                      <p className="small text-muted mb-0">
                        We handle delivery, setup, and teardown—so you can
                        actually enjoy the party.
                      </p>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex">
                    <GeoAltFill className="me-3 fs-3 text-danger" />
                    <div>
                      <h6 className="fw-bold mb-1">Local &amp; Family-Owned</h6>
                      <p className="small text-muted mb-0">
                        Based right here in your community and proud to support
                        local schools &amp; fundraisers.
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <h5 className="fw-bold mb-3">Recent Happy Customers</h5>
                  <Row className="gy-3">
                    <Col sm={6}>
                      <Card className="border-0 bg-light h-100">
                        <Card.Body>
                          <div className="d-flex align-items-center mb-2">
                            <EmojiSmile className="me-2" />
                            <strong>Carson Family</strong>
                          </div>
                          <div className="mb-2">
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                          </div>
                          <p className="small text-muted mb-0">
                            “They showed up early, set everything up, and the
                            castle was spotless. Kids jumped for hours.”
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col sm={6}>
                      <Card className="border-0 bg-light h-100">
                        <Card.Body>
                          <div className="d-flex align-items-center mb-2">
                            <PeopleFill className="me-2" />
                            <strong>Jefferson Elementary</strong>
                          </div>
                          <div className="mb-2">
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                            <StarFill className="text-warning me-1" />
                          </div>
                          <p className="small text-muted mb-0">
                            “Perfect for our school carnival. Great pricing,
                            professional staff, and the kids loved the obstacle
                            course.”
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* GALLERY / TYPES */}
      <section id="gallery" className="py-5 bg-light">
        <Container>
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-2">Bounce House Styles</h2>
            <p className="text-muted mb-0">
              Choose from colorful castles, combo units with slides, obstacle
              courses, and more.
            </p>
          </div>
          <Row className="gy-4">
            {[
              {
                title: "Rainbow Castle",
                desc: "Bright and colorful—perfect centerpiece for backyard birthdays.",
              },
              {
                title: "Princess Palace",
                desc: "Fairytale design that makes your little one feel like royalty.",
              },
              {
                title: "Sports Arena",
                desc: "Perfect for active kids who love sports and friendly competition.",
              },
            ].map((item) => (
              <Col md={4} key={item.title}>
                <Card className="h-100 shadow-sm border-0">
                  {/* Replace this div with an <img> if you have real images */}
                  <div
                    style={{
                      width: "100%",
                      height: "180px",
                      borderTopLeftRadius: "0.5rem",
                      borderTopRightRadius: "0.5rem",
                      background:
                        "repeating-linear-gradient(45deg, #0d6efd, #0d6efd 20px, #ffc107 20px, #ffc107 40px, #20c997 40px, #20c997 60px)",
                    }}
                  />
                  <Card.Body>
                    <Card.Title className="fw-bold">{item.title}</Card.Title>
                    <Card.Text className="text-muted small mb-3">
                      {item.desc}
                    </Card.Text>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      href="#quote"
                    >
                      Check availability
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* FAQ + CONTACT / QUOTE FORM */}
      <section id="faq" className="py-5">
        <Container>
          <Row className="gy-4">
            <Col md={6}>
              <h2 className="fw-bold mb-3">Frequently Asked Questions</h2>
              <div className="mb-3">
                <h6 className="fw-bold mb-1">
                  How long is a typical rental?
                </h6>
                <p className="small text-muted mb-0">
                  Our standard rentals are for 4 hours of bounce time. We set up
                  before your event and take down afterward. You can add extra
                  hours for a small fee.
                </p>
              </div>
              <div className="mb-3">
                <h6 className="fw-bold mb-1">
                  Do you require a deposit to book?
                </h6>
                <p className="small text-muted mb-0">
                  Yes, a small deposit is required to reserve your date. The
                  remaining balance is due on delivery.
                </p>
              </div>
              <div className="mb-3">
                <h6 className="fw-bold mb-1">
                  What kind of surface do you need?
                </h6>
                <p className="small text-muted mb-0">
                  Grass is ideal, but we can also set up on concrete or asphalt
                  with proper anchoring. We&apos;ll confirm setup details before
                  your event.
                </p>
              </div>
              <div className="mb-3">
                <h6 className="fw-bold mb-1">What about weather?</h6>
                <p className="small text-muted mb-0">
                  Safety comes first. If severe weather is forecast, we&apos;ll
                  work with you to reschedule or cancel with minimal hassle.
                </p>
              </div>
            </Col>

            <Col md={6} id="quote">
              <Card className="shadow-sm border-0">
                <Card.Body>
                  <h3 className="fw-bold mb-3">
                    Get Your Free Bounce House Quote
                  </h3>
                  <p className="text-muted small mb-3">
                    Tell us about your event and we’ll confirm availability and
                    pricing within one business day.
                  </p>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-3" controlId="fullName">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Your name"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-3" controlId="phone">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder="(555) 555-5555"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3" controlId="email2">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="you@example.com"
                        required
                      />
                    </Form.Group>
                    <Row>
                      <Col sm={6}>
                        <Form.Group className="mb-3" controlId="eventDate2">
                          <Form.Label>Event Date</Form.Label>
                          <Form.Control type="date" required />
                        </Form.Group>
                      </Col>
                      <Col sm={6}>
                        <Form.Group className="mb-3" controlId="cityZip">
                          <Form.Label>City / ZIP</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="City or ZIP"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3" controlId="packageInterest">
                      <Form.Label>What are you interested in?</Form.Label>
                      <Form.Select>
                        <option>Not sure yet – please recommend</option>
                        <option>Classic Castle</option>
                        <option>Slide Combo Castle</option>
                        <option>Mega Obstacle Course</option>
                        <option>Multiple Units / Large Event</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="notes">
                      <Form.Label>Anything else we should know?</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Number of kids, age range, special requests..."
                      />
                    </Form.Group>
                    <Button type="submit" variant="primary" className="w-100">
                      Request My Quote
                    </Button>
                    <p className="mt-2 mb-0 small text-muted">
                      By submitting, you agree we may contact you via phone,
                      text, or email regarding your rental.
                    </p>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="py-4 bg-dark text-light">
        <Container>
          <Row className="align-items-center gy-2">
            <Col md={6}>
              <div className="small">
                © {new Date().getFullYear()} HappyBounce Rentals. All rights
                reserved.
              </div>
              <div className="small text-muted">
                Fully insured • All equipment cleaned and sanitized after every
                event.
              </div>
            </Col>
            <Col md={6} className="text-md-end">
              <div className="small">
                <TelephoneFill className="me-1" /> (800) 555-1234 &nbsp;|&nbsp;
                <GeoAltFill className="me-1" /> Serving your local area
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default BounceHouseLandingPage_2025_11_21_chat;
